// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

/**
 * IndexedDB cache for offline-first ledger state.
 *
 * SECURITY (SEC-05): this cache NEVER stores plaintext. The full event log is
 * encrypted under the in-memory DEK by the crypto worker and only the resulting
 * ciphertext snapshot is persisted here. IndexedDB survives tab close (unlike the
 * sessionStorage-held key), so a leftover cache is opaque until the next login
 * re-derives the DEK. Encryption/decryption happens in the caller (which holds
 * the worker + key); this module is a pure opaque-bytes store.
 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

const SNAPSHOT_KEY = 'eventLog';

interface HusholdDB extends DBSchema {
  // A single encrypted snapshot of the whole event log (wire-format blob bytes).
  snapshot: {
    key: string;
    value: Uint8Array;
  };
}

let dbInstance: IDBPDatabase<HusholdDB> | null = null;

async function getDb(): Promise<IDBPDatabase<HusholdDB>> {
  if (dbInstance) return dbInstance;

  dbInstance = await openDB<HusholdDB>('hushold', 2, {
    upgrade(db, oldVersion) {
      // v1 stored DECRYPTED events + a meta store (SEC-05). Drop them outright —
      // greenfield, and we never want plaintext lingering from an old version.
      if (oldVersion < 2) {
        for (const name of Array.from(db.objectStoreNames)) {
          db.deleteObjectStore(name);
        }
        db.createObjectStore('snapshot');
      }
    },
  });

  return dbInstance;
}

/** Persist the encrypted event-log snapshot (ciphertext produced by the worker). */
export async function saveEncryptedSnapshot(bytes: Uint8Array): Promise<void> {
  const db = await getDb();
  await db.put('snapshot', bytes, SNAPSHOT_KEY);
}

/** Load the encrypted event-log snapshot, or null if none is cached. */
export async function loadEncryptedSnapshot(): Promise<Uint8Array | null> {
  const db = await getDb();
  const bytes = await db.get('snapshot', SNAPSHOT_KEY);
  return bytes ?? null;
}

/** Clear all cached data (on logout / key rotation). */
export async function clearCache(): Promise<void> {
  const db = await getDb();
  const tx = db.transaction('snapshot', 'readwrite');
  await tx.objectStore('snapshot').clear();
  await tx.done;
}
